// View-model derivation for the open-orders card view. Pure functions, so the
// card layout computes order display identically to the Open Orders table it
// mirrors (PositionTracker's inline logic — kept in lockstep deliberately).

import type { KalshiOrder } from '../../types/kalshi';
import type { PolyRelayOpenOrder } from '../../lib/polymarket/relay';
import { truncateTicker } from '../../lib/kalshiTicker/describeMarket';
import { describePolyOrderCached } from '../../lib/polymarket/describeOrder';
import { americanOdds } from '../../views/ValueDashboard/tradeModalBook';
import {
  effectivePriceForFeeAdjustedOdds,
  estimateAggregateFeeCents,
  makerRateForTicker,
} from '../../lib/pricing/fees';
import { titleParts } from './cardFormat';

export interface OpenOrderEconomics {
  /** All-in cost in dollars. Buy: stake + maker fee. Sell: proceeds − fee.
   *  Null when the side-native price isn't known. */
  costDollars: number | null;
  /** Net profit if the contract settles favorably (gross − maker fee).
   *  Null when the price isn't known. */
  toWinDollars: number | null;
  /** Maker-fee estimate in dollars. Null when the series rate isn't loaded /
   *  is `flat` / unknown — caller shows the fee-blind gross rather than a
   *  fabricated 0. Polymarket resolves to a real $0.00 (rate 0), not null. */
  feeDollars: number | null;
  /** Maker-rebate estimate in dollars. Null when the fee schedule is unknown. */
  rebateDollars: number | null;
}

/**
 * Fee-inclusive cost / to-win for a resting open order — the canonical
 * trade-panel economics (see ValueDashboard/GameDetailPanel) carried over to
 * the open-orders surfaces so they stop quoting raw qty × price.
 *
 * A resting limit bills as a MAKER fill, so the fee uses the per-series maker
 * rate through the aggregate per-fill ceil ({@link estimateAggregateFeeCents}),
 * which matches the cent-rounded figure Kalshi's own ticket quotes. Pure: the
 * caller resolves `makerRate` (Kalshi → {@link makerRateForTicker}; Polymarket
 * → 0) so this stays testable and free of the fee-cache singleton.
 *
 *   buy : cost  = stake + net fee
 *   sell: cost  = proceeds − net fee
 *   both: toWin = gross profit − net fee
 */
export function openOrderEconomics(args: {
  economicQty: number;
  bidCents: number | null;
  makerRate: number | null;
  /** MM Program maker-fee rebate fraction (0..1); reduces the maker fee.
   *  0 = no rebate. See lib/pricing/makerRebate. */
  makerRebatePct?: number;
  isSell: boolean;
}): OpenOrderEconomics {
  const { economicQty, bidCents, makerRate, makerRebatePct = 0, isSell } = args;
  if (bidCents == null) {
    return { costDollars: null, toWinDollars: null, feeDollars: null, rebateDollars: null };
  }
  const grossCost = (economicQty * bidCents) / 100;
  const grossWin = (economicQty * (100 - bidCents)) / 100;
  if (makerRate == null) {
    // Rate not loaded / flat / unknown series — show fee-blind gross rather
    // than invent a fee (memory: a null rate renders "—"/raw, never 0).
    return { costDollars: grossCost, toWinDollars: grossWin, feeDollars: null, rebateDollars: null };
  }
  const feeDollars =
    estimateAggregateFeeCents({ priceCents: bidCents, count: economicQty, feeRate: makerRate }) /
    100;
  const rebateDollars =
    makerRebatePct > 0 ? feeDollars * Math.min(makerRebatePct, 1) : 0;
  const netFeeDollars = feeDollars - rebateDollars;
  return {
    costDollars: isSell ? grossCost - netFeeDollars : grossCost + netFeeDollars,
    toWinDollars: grossWin - netFeeDollars,
    feeDollars,
    rebateDollars,
  };
}

interface BookLevelLite {
  price: number;
  size: number;
}
interface BookLite {
  yes: BookLevelLite[];
  no: BookLevelLite[];
}

export interface OrderCardVM {
  key: string;
  /** Venue-native order id for the cancel call. */
  cancelId: string;
  venue: 'kalshi' | 'polymarket';
  ticker: string;
  title: string;
  subtitle: string;
  eventStartLabel: string | null;
  side: 'yes' | 'no' | null;
  team: string | null;
  /** Partially filled. */
  filling: boolean;
  /** Partially filled while no better level is ahead; drives the amber in-sale UI. */
  activeInSale: boolean;
  isSell: boolean;
  totalQty: number;
  filledQty: number;
  remainingQty: number;
  bidCents: number | null;
  bidOdds: string | null;
  lastCents: number | null;
  lastOdds: string | null;
  /** 0 = at front of book, N = behind by N¢, null = unknown. */
  bookDiff: number | null;
  /** Absolute position in the FIFO — at-level slot plus everything resting
   *  at strictly better prices. 0 = literal front of book. */
  queue: number | null;
  /** Just the strictly-better levels — total contracts at price(s) above
   *  ours that will fill before our level is even touched. 0 = top of book
   *  (no levels above us). */
  queueAbove: number | null;
  /** Contracts at our exact price level resting behind us in the FIFO
   *  (at-level depth − atLevel). 0 = nobody queued behind us. */
  queueBehind: number | null;
  queueTotal: number | null;
  /** All-in cost incl. maker fee (buy: stake + fee; sell: proceeds − fee). */
  costDollars: number | null;
  /** Net profit if settled favorably (gross − maker fee). */
  toWinDollars: number | null;
  /** Maker-fee estimate in dollars; null when the series rate isn't loaded. */
  feeDollars: number | null;
  restingSinceMs: number | null;
  /** "GTC" or a local expiry time. */
  tifLabel: string;
  /** Polymarket-only replacement hints. Forwarded so edit-price can preserve
   *  fine-tick and NEG_RISK market settings after canceling the old order. */
  tickSize?: string;
  negRisk?: boolean;
}

/** Side-native bid price in cents. Kalshi often sends only yes_price even for
 *  a NO order, so a NO bid is 100 − yes_price. */
function bidCentsOf(order: KalshiOrder): number | null {
  return order.side === 'yes'
    ? (order.yesPrice ?? (order.noPrice != null ? 100 - order.noPrice : null))
    : (order.noPrice ?? (order.yesPrice != null ? 100 - order.yesPrice : null));
}

export interface KalshiOrderCtx {
  /** getLastTradeYesCents(ticker) */
  lastTradeYesCents: number | null;
  /** getOrderbookRaw(ticker) */
  book: BookLite | null;
  /** Cumulative queue ahead of this order — at-level slot plus everything
   *  resting at strictly better prices. */
  queue: number | null;
  /** Strictly-better levels only (no at-level slot). */
  queueAbove: number | null;
  /** Contracts at our price level behind us in the FIFO. */
  queueBehind: number | null;
  /** Total resting depth in the book — the queue percentile denominator. */
  queueTotal: number | null;
  /** Scheduled event start for the order's market, local-display formatted by caller. */
  eventStartLabel?: string | null;
  /** Scheduled event start epoch ms, used only to label matching expirations as GTS. */
  eventStartMs?: number | null;
  /** Actual matched contracts for this order. Prefer fills over initial-minus-remaining,
   *  because queue-preserving decreases leave Kalshi's initial_count at the old size. */
  filledQty?: number | null;
  /** Whether American odds labels should include maker fees. */
  feeAdjustedOdds?: boolean;
  /** MM Program maker-fee rebate fraction (0..1) to fold into maker displays.
   *  0 = no rebate. */
  makerRebatePct?: number;
}

export function buildKalshiOrderVM(
  order: KalshiOrder,
  ctx: KalshiOrderCtx,
): OrderCardVM {
  const { title, subtitle, team } = titleParts(order.ticker, order.side);
  const bidCents = bidCentsOf(order);
  const isSell = order.action === 'sell';

  // Last trade — hydra YES-cents, inverted for a NO order.
  const lastCents =
    ctx.lastTradeYesCents == null
      ? null
      : order.side === 'yes'
        ? ctx.lastTradeYesCents
        : 100 - ctx.lastTradeYesCents;

  // Book reference: a BUY compares to same-side bids; a SELL to the implied
  // ask (100 − opposite-side bid). bookDiff 0 = at front, N = behind by N¢.
  let topRef: number | null = null;
  if (ctx.book) {
    const refLevels = isSell
      ? order.side === 'yes'
        ? ctx.book.no
        : ctx.book.yes
      : order.side === 'yes'
        ? ctx.book.yes
        : ctx.book.no;
    if (refLevels.length > 0) {
      let max = -Infinity;
      for (const lvl of refLevels) if (lvl.price > max) max = lvl.price;
      if (Number.isFinite(max)) topRef = isSell ? 100 - max : max;
    }
  }
  const bookDiff =
    topRef != null && bidCents != null
      ? Math.max(0, isSell ? bidCents - topRef : topRef - bidCents)
      : null;

  const remainingQty = order.remainingCount;
  const hasFillOverride = ctx.filledQty != null;
  const filledQty = hasFillOverride
    ? Math.max(0, Math.round(ctx.filledQty ?? 0))
    : Math.max(0, order.count - remainingQty);
  const totalQty = hasFillOverride ? remainingQty + filledQty : order.count;
  // Economics on the filled portion once anything has filled, else on the
  // whole order — matches the trader mockup's cost/to-win figures.
  const economicQty = filledQty > 0 ? filledQty : totalQty;
  // Resting limit → maker fill; fold the per-series maker fee into the
  // displayed economics so the card matches the trade panel / Kalshi ticket.
  const econ = openOrderEconomics({
    economicQty,
    bidCents,
    makerRate: makerRateForTicker(order.ticker),
    makerRebatePct: ctx.makerRebatePct,
    isSell,
  });
  const feeAdjustedBidCents =
    bidCents == null
      ? null
      : effectivePriceForFeeAdjustedOdds(
          bidCents,
          ctx.feeAdjustedOdds ?? false,
          makerRateForTicker(order.ticker),
          ctx.makerRebatePct ?? 0,
        );
  const feeAdjustedLastCents =
    lastCents == null
      ? null
      : effectivePriceForFeeAdjustedOdds(
          Math.round(lastCents),
          ctx.feeAdjustedOdds ?? false,
          makerRateForTicker(order.ticker),
          ctx.makerRebatePct ?? 0,
        );

  let tifLabel = 'GTC';
  if (order.expirationTs != null) {
    const expMs = order.expirationTs * 1000;
    tifLabel =
      ctx.eventStartMs != null && Math.abs(expMs - ctx.eventStartMs) <= 60_000
        ? 'GTS'
        : new Date(expMs).toLocaleTimeString([], {
            hour: 'numeric',
            minute: '2-digit',
          });
  }

  return {
    key: order.orderId,
    cancelId: order.orderId,
    venue: 'kalshi',
    ticker: order.ticker,
    title,
    subtitle,
    eventStartLabel: ctx.eventStartLabel ?? null,
    side: order.side,
    team,
    filling: filledQty > 0,
    activeInSale: filledQty > 0 && ctx.queueAbove === 0,
    isSell,
    totalQty,
    filledQty,
    remainingQty,
    bidCents,
    bidOdds: feeAdjustedBidCents != null ? americanOdds(feeAdjustedBidCents) : null,
    lastCents,
    lastOdds: feeAdjustedLastCents != null ? americanOdds(feeAdjustedLastCents) : null,
    bookDiff,
    queue: ctx.queue,
    queueAbove: ctx.queueAbove,
    queueBehind: ctx.queueBehind,
    queueTotal: ctx.queueTotal,
    costDollars: econ.costDollars,
    toWinDollars: econ.toWinDollars,
    feeDollars: econ.feeDollars,
    restingSinceMs: order.createdTime ? Date.parse(order.createdTime) : null,
    tifLabel,
  };
}

export function buildPolyOrderVM(order: PolyRelayOpenOrder): OrderCardVM {
  const id = String(order.id ?? order.orderID ?? order.orderId ?? '');
  const assetId = String(order.asset_id ?? order.market ?? id);
  const isSell = String(order.side ?? '').toUpperCase() === 'SELL';
  const priceFrac =
    typeof order.price === 'number' ? order.price : Number(order.price ?? NaN);
  const bidCents = Number.isFinite(priceFrac) ? Math.round(priceFrac * 100) : null;
  const orig =
    typeof order.original_size === 'number'
      ? order.original_size
      : Number(order.original_size ?? 0);
  const matched =
    typeof order.size_matched === 'number'
      ? order.size_matched
      : Number(order.size_matched ?? 0);
  const remainingQty = Math.max(0, orig - matched);
  const filledQty = Math.max(0, matched);
  const economicQty = filledQty > 0 ? filledQty : orig;
  // Polymarket has no maker fee in our model → rate 0 (a real $0.00, not an
  // unknown). Keeps the same fee-inclusive shape as the Kalshi path.
  const econ = openOrderEconomics({
    economicQty,
    bidCents,
    makerRate: 0,
    isSell,
  });

  // Resolve the asset_id (tokenId) to a human market description via the
  // cached marketStore lookup (this builder runs per order per page tick —
  // the label path; the replace flow re-derives tickSize/negRisk FRESH at
  // click time). Falls back to the truncated hex tokenId until the sport
  // loads; unresolved ids retry on the cache's wall-clock cadence.
  const desc = describePolyOrderCached(assetId);
  const title = desc
    ? `${desc.matchup} — ${desc.sport.toUpperCase()} — ${desc.date.slice(5)}`
    : truncateTicker(assetId);
  const subtitle = desc
    ? `(${desc.betTeam}) ${desc.side.toUpperCase()}`
    : truncateTicker(assetId);

  return {
    key: `poly-${id}`,
    cancelId: id,
    venue: 'polymarket',
    ticker: assetId,
    title,
    subtitle,
    eventStartLabel: null,
    side: desc?.side ?? null,
    team: desc?.betTeam ?? null,
    filling: filledQty > 0,
    activeInSale: filledQty > 0,
    isSell,
    totalQty: orig,
    filledQty,
    remainingQty,
    bidCents,
    bidOdds: bidCents != null ? americanOdds(bidCents) : null,
    lastCents: null,
    lastOdds: null,
    bookDiff: null,
    queue: null,
    queueAbove: null,
    queueBehind: null,
    queueTotal: null,
    costDollars: econ.costDollars,
    toWinDollars: econ.toWinDollars,
    feeDollars: econ.feeDollars,
    restingSinceMs: null,
    tifLabel: 'GTC',
    tickSize: desc?.tickSize,
    negRisk: desc?.negRisk,
  };
}

// Queue depth tiers — absolute thresholds (per trader spec):
//   queue 0 = front of book (pulse), <50k = green, 50–300k = yellow,
//   ≥300k = red. `total` is no longer needed for tiering but is kept on
//   the tone helpers' signatures so existing call sites don't have to
//   change.
type QueueTier = 'unknown' | 'front' | 'green' | 'yellow' | 'red';

function queueTier(queue: number | null): QueueTier {
  if (queue == null) return 'unknown';
  if (queue === 0) return 'front';
  if (queue < 50_000) return 'green';
  if (queue < 300_000) return 'yellow';
  return 'red';
}

/** Queue depth -> value tone. Used in both card and table views so the
 *  queue number reads identically; only the layout surface changes. */
export function queueTone(
  queue: number | null,
  _total: number | null,
): string {
  switch (queueTier(queue)) {
    case 'unknown':
      return 'text-muted-foreground';
    case 'front':
    case 'green':
      return 'text-green-400';
    case 'yellow':
      return 'text-yellow-400';
    case 'red':
      return 'text-red-400';
  }
}

/** In-line-behind depth -> value tone. Unlike queue/level-above, larger is
 *  better here because more contracts are resting behind the user's order. */
export function queueBehindTone(queueBehind: number | null): string {
  if (queueBehind == null) return 'text-muted-foreground';
  if (queueBehind <= 30_000) return 'text-red-400';
  if (queueBehind < 150_000) return 'text-yellow-400';
  return 'text-green-400';
}

export function queueBehindAtPriceLevel(
  atLevelDepth: number,
  atLevelAhead: number,
  ownRemainingQty: number,
): number {
  return Math.max(0, atLevelDepth - atLevelAhead - ownRemainingQty);
}

/** Queue depth -> table row tint. */
export function queueRowTone(
  queue: number | null,
  _total: number | null,
): string {
  switch (queueTier(queue)) {
    case 'front':
      return 'animate-queue-front-pulse';
    case 'green':
      return 'bg-green-500/20';
    case 'yellow':
      return 'bg-yellow-500/20';
    case 'red':
      return 'bg-red-500/20';
    default:
      return '';
  }
}

/** Queue depth -> card container rail (left accent strip). */
export function queueCardTone(
  queue: number | null,
  _total: number | null,
): string {
  switch (queueTier(queue)) {
    case 'front':
    case 'green':
      return 'bg-green-400';
    case 'yellow':
      return 'bg-yellow-400';
    case 'red':
      return 'bg-red-400';
    default:
      return '';
  }
}

/** Queue-front value pulse. Applied to the value itself; table/card
 *  container treatments remain separate. */
export function queueValuePulse(queue: number | null): string {
  return queue === 0 ? 'animate-queue-front-text-pulse' : '';
}
